import { IneligibilityReason } from '@ocx-app/modules/offers/eligibility.types';
import { IntlShape } from 'react-intl';
import { OfferEngagementType, OfferVisibleStatus } from '@ocx/graphql';
import { Offer } from '../../modules/offers/offer.types';
import { messages } from './offer-details.messages';
import { Coordinates } from '../../modules/stores/types';
import { OfferDetailsAvailableAtLocation } from '../../components/OfferDetails/OfferDetailsLocationsCarousel';
import { calculateDistance } from '../../lib/geolocation/utils/calculateDistance';
import { OfferWithSanitizedLocations } from '../../modules/offers/use-offer.hook';

export const isOfferVisible = (offer: Offer) => {
  const { engagementType, visibleStatus } = offer;

  if (
    // Only offers with visibleStatus === VISIBLE allowed
    visibleStatus === OfferVisibleStatus.Visible
  ) {
    return true;
  }

  if (
    // Vouchered games allowed to be shown when no vouchers left
    // required for animation finalising and purchasing additional gameplay
    engagementType === OfferEngagementType.GameVoucher &&
    visibleStatus === OfferVisibleStatus.NoVoucher
  ) {
    return true;
  }

  if (
    // Limited Game's offers require animation and should stay visible.
    engagementType === OfferEngagementType.Game &&
    [OfferVisibleStatus.MemberLimit, OfferVisibleStatus.GlobalLimit].includes(visibleStatus)
  ) {
    return true;
  }

  return false;
};

export const getOfferIneligibilityWarningMessage = (reason: IneligibilityReason, intl: IntlShape): string | null => {
  if (reason.type === 'purchasable_eligibility_constraint_failed') {
    return intl.formatMessage(messages.offerDetailsIneligibilityReasonPurchasableEligibilityConstraintFailed);
  }
  return null;
};

export const getAvailableAtLocations = (
  offer: OfferWithSanitizedLocations | null,
  memberCoordinates: Coordinates | null,
) => {
  if (!offer) {
    return [];
  }

  const locations: OfferDetailsAvailableAtLocation[] = offer.availableAtLocations.map<OfferDetailsAvailableAtLocation>(
    (location) => ({
      uuid: location.uuid,
      name: location.name || location.address.street1,
      address: `${location.address.street1}, ${location.address.city}, ${location.address.state}`,
      coordinates: { lat: location.address.geoLocation.latitude, lng: location.address.geoLocation.longitude },
    }),
  );

  if (memberCoordinates) {
    locations.sort((a, b) => {
      const distanceToA = calculateDistance(memberCoordinates, a.coordinates);
      const distanceToB = calculateDistance(memberCoordinates, b.coordinates);
      return distanceToA - distanceToB;
    });
  }

  return locations;
};
